import {
  resolveShipToInventory,
  resolveShipToPrice,
  resolveShipToSubscribers,
  resolveShipToWebStatus,
  resolveShipToWholesalePrice,
} from "@bcc/lib/products/ship-to-countries";
import { parsePrice } from "@bcc/lib/utils";
import type { VendorCountryCode } from "@shared/types";
import { ColumnDef, FilterFn, SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  ActionsCellMarkup,
  BagSizeCellMarkup,
  ExpectedOutOfStockCellMarkup,
  ImageCellMarkup,
  MsrpRrpCellMarkup,
  NameCellMarkup,
  RequestsCellMarkup,
  ShipToCellMarkup,
  StatusCellMarkup,
  SubscribersCellMarkup,
  TermCellMarkup,
  VariantsCellMarkup,
  WeeklyLimitCellMarkup,
  WholesalePriceCellMarkup,
} from "./cells";
import type { ProductRowPopoverType, VariantWithProductData } from "./types";

// Custom sorting function that maintains product grouping.
// Sorts by accessor value first, then uses productId as tiebreaker to keep variants grouped.
const createGroupedSortingFn = <T extends string | number>(
  accessor: (row: VariantWithProductData) => T
): SortingFn<VariantWithProductData> => {
  return (rowA, rowB) => {
    const a = accessor(rowA.original);
    const b = accessor(rowB.original);

    if (a < b) return -1;
    if (a > b) return 1;

    // Same accessor value — use productId to keep variants of the same product together
    const productIdA = rowA.original.productId;
    const productIdB = rowB.original.productId;
    if (productIdA < productIdB) return -1;
    if (productIdA > productIdB) return 1;

    return 0;
  };
};

// Distinct PIM web statuses that the Status cell renders under one label, so
// filtering and sorting group them the way the badge does. "Visible but not
// for Sale" (on the storefront, not buyable) and "Temporarily Out of Stock"
// (not currently purchasable) show as plain "Out of Stock", and there is no
// separate chip for either.
const WEB_STATUS_DISPLAY_ALIASES: Record<string, string> = {
  "Visible but not for Sale": "Out of Stock",
  "Temporarily Out of Stock": "Out of Stock",
};

// Resolves the status the Status cell actually displays: cross-border SKUs
// carry a per-country web status in `shipToInfo`, so the vendor's countryCode
// status wins, falling back to the row-level resolved `webStatus`. Both the
// status filter and sort must use this so they match the visible badge rather
// than the raw row-level `webStatus`.
const resolveDisplayedWebStatus = (row: VariantWithProductData, countryCode: VendorCountryCode | undefined): string => {
  const resolved = resolveShipToWebStatus(row.shipToInfo, countryCode, row.webStatus) ?? "";
  return WEB_STATUS_DISPLAY_ALIASES[resolved] ?? resolved;
};

// Resolves the retail price the MSRP/RRP cell actually displays: cross-border
// SKUs carry a per-country price in `shipToInfo`, and on PIM-first rows that is
// the only place an approved Price Update lands — the flat `price` comes from
// the staler Salesforce overlay. Sorting must key on the displayed value or the
// header orders rows by hidden Salesforce figures.
//
// Deliberately NOT mirrored from the cell: its secondary `data.shipTo` fallback
// (`homeShipTo?.retailPrice` / `homeShipTo?.wholesalePrice`) and the wholesale
// cell's `displayWholesaleForRegion` tax adjustment. Both need inputs the column
// factory never receives — the vendor's `region`, which `homeRegionForVendor`
// needs to pick the home-region entry, and the tax-rates context — and
// `data.shipTo` is a derived breakdown the product fetch does not populate, so
// the flat scalar is what the cells fall back to on every real row today.
// Sorting stays on the flat scalar for those cases, matching the cell.
const resolveDisplayedPrice = (row: VariantWithProductData, countryCode: VendorCountryCode | undefined): number =>
  parsePrice(resolveShipToPrice(row.shipToInfo, countryCode, row.price));

// Wholesale counterpart of `resolveDisplayedPrice`; same precedence and the same
// deliberate omission of the `data.shipTo` / tax-adjusted display fallbacks.
const resolveDisplayedWholesalePrice = (row: VariantWithProductData, countryCode: VendorCountryCode | undefined): number =>
  parsePrice(resolveShipToWholesalePrice(row.shipToInfo, countryCode, row.wholesalePrice));

// Resolves the figure the Weekly Limit cell actually displays: the logged-in
// country's per-channel `inventoryQuantity` (which reflects an approved
// Inventory Update), falling back to the same single-valued chain as the cell.
// SS-sourced rows populate `maxBagsShippedPerWeek` (from the SF sanitizer)
// without `weeklyInventory`; standard PIM-first rows prefer PIM `weeklyInventory`
// with Salesforce as fallback.
const resolveDisplayedWeeklyLimit = (row: VariantWithProductData, countryCode: VendorCountryCode | undefined): number =>
  resolveShipToInventory(row.shipToInfo, countryCode, row.weeklyInventory ?? row.maxBagsShippedPerWeek ?? 0);

// Resolves the subscriber count the Subscribers cell actually displays: cross-
// border SKUs carry a per-country count in `shipToInfo`, so the vendor's
// countryCode entry wins, falling back to the row-level `activeSubscriberCount`.
// The sort must use this so it matches the visible number rather than the raw
// (often-empty, for cross-border rows) row-level field.
const resolveDisplayedSubscribers = (row: VariantWithProductData, countryCode: VendorCountryCode | undefined): number =>
  resolveShipToSubscribers(row.shipToInfo, countryCode, row.activeSubscriberCount ?? 0);

// Status column filter: compares the filter chip's value against the
// per-country displayed status, not the raw `webStatus` accessor. Without this
// a DE "Out of Stock" SKU whose row-level `webStatus` is "Visible for Sale"
// would leak into the "Visible for Sale" filter (and vice versa).
const createStatusFilterFn =
  (countryCode: VendorCountryCode | undefined): FilterFn<VariantWithProductData> =>
  (row, _columnId, filterValue) => {
    if (typeof filterValue !== "string" || filterValue === "") return true;
    return resolveDisplayedWebStatus(row.original, countryCode) === filterValue;
  };

// Sorting function for the name column: sorts by productTitle, then productId as tiebreaker
const nameSortingFn: SortingFn<VariantWithProductData> = (rowA, rowB) => {
  const titleA = (rowA.original.productTitle ?? "").toLowerCase();
  const titleB = (rowB.original.productTitle ?? "").toLowerCase();
  if (titleA < titleB) return -1;
  if (titleA > titleB) return 1;
  // Tiebreaker: keep variants of the same product together
  const idA = rowA.original.productId;
  const idB = rowB.original.productId;
  if (idA < idB) return -1;
  if (idA > idB) return 1;
  return 0;
};

// i18n key for a column's display name. Exported so the DataTable columns
// toggle menu can label its checkbox items with the same copy as the header.
export const getColumnLabelKey = (columnId: string): `views.productsTable.columns.${string}` => `views.productsTable.columns.${columnId}`;

// Header cell component (simple text header)
const HeaderCell = ({ column }: { column: { id: string } }) => {
  const { t } = useTranslation();
  return (
    <span>
      {t(getColumnLabelKey(column.id), {
        defaultValue: column.id,
      })}
    </span>
  );
};

interface ColumnOptions {
  showVariantsColumn?: boolean;
  // Cross-border "Ship to" column. Driven by whether the vendor's
  // `properties.shipTo` lists cross-border destinations (NL/DE) — see
  // products-table.tsx. Off by default for non-cross-border vendors.
  showShipToColumn?: boolean;
  // Vendor's countryCode, used to resolve the per-country status for the
  // status column's filter/sort so they match the displayed badge.
  currentCountryCode?: VendorCountryCode;
  // ShipStation roasters don't get the per-row Actions column (disconnect/map product).
  isShipstationRoaster?: boolean;
  onConfigureDraft?: (productId: string | number) => void;
  onImageClick?: (productId: string | number) => void;
  getPopoverOpen?: (type: ProductRowPopoverType, variantId: string) => boolean;
  onPopoverOpenChange?: (type: ProductRowPopoverType, variantId: string, open: boolean) => void;
  onDisconnect?: (variantId: string) => void;
  isDisconnectVisible?: (variantId: string) => boolean;
  isDisconnecting?: boolean;
  onMapProduct?: (variantId: string) => void;
  isMapProductVisible?: (variantId: string) => boolean;
  // Manual expansion control
  expandedProductIds?: Set<string>;
  expandableProducts?: Set<string>;
  firstVariantIds?: Set<string>;
  onToggleExpansion?: (productId: string) => void;
}

export const createColumns = (options: ColumnOptions = {}): ColumnDef<VariantWithProductData>[] => {
  const {
    showVariantsColumn = true,
    showShipToColumn = false,
    currentCountryCode,
    isShipstationRoaster = false,
    onConfigureDraft,
    onImageClick,
    getPopoverOpen,
    onPopoverOpenChange,
    onDisconnect,
    isDisconnectVisible,
    isDisconnecting = false,
    onMapProduct,
    isMapProductVisible,
    expandedProductIds,
    expandableProducts,
    firstVariantIds,
    onToggleExpansion,
  } = options;

  const baseColumns: ColumnDef<VariantWithProductData>[] = [
    {
      id: "image",
      accessorKey: "imageUrl",
      header: HeaderCell,
      // Carries the multi-variant expand chevron — hiding it would break row expansion.
      enableHiding: false,
      cell: (props) => {
        const productId = String(props.row.original.productId);
        const variantId = props.row.original.id;
        const isExpanded = expandedProductIds?.has(productId) ?? false;
        const isFirstVariant = firstVariantIds?.has(variantId) ?? true;
        // Only show chevron on first variant of expandable products
        const canExpand = (expandableProducts?.has(productId) && isFirstVariant) ?? false;

        return (
          <ImageCellMarkup
            {...props}
            onImageClick={() => onImageClick?.(props.row.original.productId ?? 0)}
            onToggleExpansion={() => onToggleExpansion?.(productId)}
            isExpanded={isExpanded}
            canExpand={canExpand}
            isFirstVariant={isFirstVariant}
          />
        );
      },
      enableSorting: false,
    },
    {
      id: "name",
      accessorKey: "productTitle",
      header: HeaderCell,
      // Identity column.
      enableHiding: false,
      cell: (props) => {
        const variantId = props.row.original.id;
        const isFirstVariant = firstVariantIds?.has(variantId) ?? true;

        return <NameCellMarkup {...props} isFirstVariant={isFirstVariant} />;
      },
      sortingFn: nameSortingFn,
    },
    {
      id: "bagSize",
      accessorKey: "bagSize",
      header: HeaderCell,
      // Target of the bag-size filter chips.
      enableHiding: false,
      cell: BagSizeCellMarkup,
      size: 160,
      sortingFn: createGroupedSortingFn((row) => row.bagSize ?? ""),
    },
    {
      id: "status",
      accessorKey: "webStatus",
      header: HeaderCell,
      // Target of the status filter chips.
      enableHiding: false,
      cell: (props) => (
        <StatusCellMarkup
          {...props}
          open={getPopoverOpen?.("status", props.row.original.id)}
          onOpenChange={(open) => onPopoverOpenChange?.("status", props.row.original.id, open)}
          onConfigureDraft={onConfigureDraft ? () => onConfigureDraft(props.row.original.productId ?? 0) : undefined}
        />
      ),
      filterFn: createStatusFilterFn(currentCountryCode),
      sortingFn: createGroupedSortingFn((row) => resolveDisplayedWebStatus(row, currentCountryCode)),
    },
  ];

  // Cross-border "Ship to" column. Sits between status and variants to match
  // the column order in the i18n bundle. Only present for cross-border
  // vendors (gated by `showShipToColumn` from products-table.tsx).
  if (showShipToColumn) {
    baseColumns.push({
      id: "shipTo",
      accessorKey: "shipTo",
      header: HeaderCell,
      cell: ShipToCellMarkup,
      enableSorting: false,
    });
  }

  // Conditionally add variants column
  if (showVariantsColumn) {
    baseColumns.push({
      id: "variants",
      accessorKey: "grind",
      header: HeaderCell,
      cell: VariantsCellMarkup,
      enableSorting: false,
    });
  }

  // Add remaining columns
  const remainingColumns: ColumnDef<VariantWithProductData>[] = [
    {
      id: "subscribers",
      accessorKey: "activeSubscriberCount",
      header: HeaderCell,
      cell: SubscribersCellMarkup,
      sortingFn: createGroupedSortingFn((row) => resolveDisplayedSubscribers(row, currentCountryCode)),
    },
    {
      id: "wholesalePrice",
      accessorKey: "wholesalePrice",
      header: HeaderCell,
      cell: (props) => (
        <WholesalePriceCellMarkup
          {...props}
          open={getPopoverOpen?.("price-wholesale", props.row.original.id)}
          onOpenChange={(open) => onPopoverOpenChange?.("price-wholesale", props.row.original.id, open)}
        />
      ),
      // Sort on the country-aware value the cell renders, not the flat scalar.
      sortingFn: createGroupedSortingFn((row) => resolveDisplayedWholesalePrice(row, currentCountryCode)),
    },
    {
      id: "msrpRrp",
      accessorKey: "price",
      header: HeaderCell,
      cell: (props) => (
        <MsrpRrpCellMarkup
          {...props}
          open={getPopoverOpen?.("price-msrp", props.row.original.id)}
          onOpenChange={(open) => onPopoverOpenChange?.("price-msrp", props.row.original.id, open)}
        />
      ),
      // Sort on the country-aware value the cell renders, not the flat scalar.
      sortingFn: createGroupedSortingFn((row) => resolveDisplayedPrice(row, currentCountryCode)),
    },
    {
      id: "weeklyLimit",
      accessorKey: "weeklyInventory",
      header: HeaderCell,
      cell: (props) => (
        <WeeklyLimitCellMarkup
          {...props}
          open={getPopoverOpen?.("inventory", props.row.original.id)}
          onOpenChange={(open) => onPopoverOpenChange?.("inventory", props.row.original.id, open)}
        />
      ),
      // Sort on the country-aware value the cell renders, not the flat scalar:
      // cross-border rows show the per-country `inventoryQuantity`. SS-sourced
      // rows populate `maxBagsShippedPerWeek` (from the SF sanitizer) without
      // `weeklyInventory`, so the sort key falls back to the former. Standard
      // PIM-first rows prefer PIM `weeklyInventory` with Salesforce as fallback.
      sortingFn: createGroupedSortingFn((row) => resolveDisplayedWeeklyLimit(row, currentCountryCode)),
    },
    {
      id: "term",
      accessorKey: "term",
      header: HeaderCell,
      cell: TermCellMarkup,
      sortingFn: createGroupedSortingFn((row) => row.term ?? ""),
    },
    {
      id: "expectedOutOfStock",
      accessorKey: "expectedOutOfStockDate",
      header: HeaderCell,
      cell: ExpectedOutOfStockCellMarkup,
      sortingFn: createGroupedSortingFn((row) => row.termEndDate ?? row.endDate ?? row.expectedOutOfStockDate ?? ""),
    },
    {
      id: "requests",
      accessorKey: "id",
      header: HeaderCell,
      cell: RequestsCellMarkup,
      enableSorting: false,
    },
  ];

  if (!isShipstationRoaster) {
    remainingColumns.push({
      id: "actions",
      accessorKey: "productId",
      header: HeaderCell,
      // Row action buttons.
      enableHiding: false,
      cell: (props) => {
        const variantId = props.row.original.id;
        const productId = String(props.row.original.productId);
        const isExpandable = expandableProducts?.has(productId) ?? false;
        const isFirstVariant = firstVariantIds?.has(variantId) ?? true;
        const isExpanded = expandedProductIds?.has(productId) ?? false;
        // Multi-bag product collapsed: first variant row shows generic "Actions"
        // button instead of per-variant disconnect/map buttons.
        const isCollapsed = isExpandable && isFirstVariant && !isExpanded;
        const canDisconnect = !isCollapsed && onDisconnect && (isDisconnectVisible?.(variantId) ?? false);
        const canMapProduct = !isCollapsed && onMapProduct && (isMapProductVisible?.(variantId) ?? false);
        return (
          <ActionsCellMarkup
            {...props}
            onDisconnect={canDisconnect ? () => onDisconnect(variantId) : undefined}
            disconnectDisabled={isDisconnecting}
            onMapProduct={canMapProduct ? () => onMapProduct(variantId) : undefined}
            isCollapsed={isCollapsed}
            onExpandActions={isCollapsed ? () => onToggleExpansion?.(productId) : undefined}
          />
        );
      },
      enableSorting: false,
    });
  }

  return [...baseColumns, ...remainingColumns];
};

// Default columns export for simple usage
export const columns = createColumns();
